// src/pages/Home/Home.jsx

import React from 'react';
import { useNavigate } from 'react-router-dom';
import HeaderLogo from 'assets/headerAssets/svg/HeaderLogo';
import './Homepage.scss';
import { BellRing, CalendarDays, CalendarSync, ChartColumn, ChartColumnIncreasing, Check, ClipboardList, Mail, MapPinHouse, Phone, Rocket, Search, Settings, Target, Users, Zap } from 'lucide-react';

const Homepage = () => {
    const navigate = useNavigate();

    return (
        <div className="home-page">
            {/* Header */}
            <header className="home-header">
                <div className="header-content">
                    <div className="logo-section" onClick={() => navigate('/home')}>
                        <HeaderLogo />
                    </div>

                    <nav className="nav-links">
                        <a
                            href={`${process.env.REACT_APP_FRONTEND}/privacy-policy`}
                            className="nav-link"
                        >
                            Privacy Policy
                        </a>

                        <a
                            href={`${process.env.REACT_APP_FRONTEND}/terms-of-service`}
                            className="nav-link"
                        >
                            Terms & Conditions
                        </a>

                        <a
                            href={`${process.env.REACT_APP_FRONTEND}/login`}
                            className="nav-link login-btn"
                        >
                            Login
                        </a>
                    </nav>
                </div>
            </header>

            {/* Main Content */}
            <main className="home-main">
                <div className="hero-section">
                    <h1 className="hero-title">Welcome to InspectXpert</h1>
                    <p className="hero-subtitle">
                        Smart, Flexible Workflow Management for Modern Teams
                    </p>
                </div>

                <div className="content-container-home">
                    <section className="product-description">
                        <h2>About InspectXpert</h2>
                        <p>
                            <strong>InspectXpert</strong> is a smart, flexible workflow management system that streamlines
                            projects and boosts team collaboration. It lets you create
                            multiple boards, customize workflow columns, and manage tasks or tickets seamlessly from
                            start to finish.
                        </p>

                        <p>
                            We use InspectXpert because it automates recurring tasks, simplifies meeting scheduling,
                            and keeps everything on track with zero manual effort. Its real-time KPI dashboard provides
                            clear insights into productivity and performance, helping teams stay organized, accountable,
                            and efficient.
                        </p>

                        <div className="features-grid">
                            <div className="feature-card">
                                <div className='feature-icon'><ClipboardList size={48} /></div>

                                <h3>Unlimited Boards</h3>
                                <p>Create unlimited boards with customizable workflows</p>
                            </div>

                            <div className="feature-card">
                                <div className="feature-icon"><Check size={48} /></div>
                                <h3>Task Tracking</h3>
                                <p>Track tasks and tickets with clear ownership and priorities</p>
                            </div>

                            <div className="feature-card">
                                <div className="feature-icon"><Settings size={48} /></div>
                                <h3>Automation</h3>
                                <p>Automate recurring tasks with zero manual effort</p>
                            </div>

                            <div className="feature-card">
                                <div className="feature-icon"><CalendarDays size={48} /></div>
                                <h3>Meeting Scheduling</h3>
                                <p>Schedule meetings and track action items easily</p>
                            </div>

                            <div className="feature-card">
                                <div className="feature-icon"><BellRing size={48} /></div>
                                <h3>Real-time Alerts</h3>
                                <p>Get instant notifications for deadlines and updates</p>
                            </div>

                            <div className="feature-card">
                                <div className="feature-icon"><ChartColumnIncreasing size={48} /></div>
                                <h3>Analytics Dashboard</h3>
                                <p>Real-time visibility into task progress and productivity</p>
                            </div>
                        </div>
                    </section>

                    <section className="google-calendar-section">
                        <h2>Google Calendar Integration</h2>
                        <div className="calendar-content">
                            <div className="calendar-icon"><CalendarSync size={80} /></div>
                            <div className="calendar-text">
                                <p>
                                    <strong>Never miss a deadline again!</strong> InspectXpert seamlessly integrates with
                                    Google Calendar to automatically sync all your tickets, meetings, and reminders directly
                                    to your calendar.
                                </p>
                                <p>
                                    When you create a task or schedule a meeting in InspectXpert, it instantly appears in
                                    your Google Calendar, ensuring you stay on top of everything without manual updates.
                                    Your team members get calendar invites automatically, and all changes sync in real-time.
                                </p>
                                <ul className="calendar-benefits">
                                    <li>✓ Automatic synchronization of tickets, meetings, and reminders</li>
                                    <li>✓ Instant calendar invites for all team members</li>
                                    <li>✓ Real-time updates across both platforms</li>
                                    <li>✓ Control which events sync with customizable preferences</li>
                                    <li>✓ Disconnect anytime with one click from your profile</li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    <section className="insights-section">
                        <h2>Powerful Insights & Analytics</h2>
                        <p>
                            InspectXpert provides real-time visibility into task progress and productivity, offers
                            detailed team performance analytics, highlights pending workloads and bottlenecks, and
                            tracks SLA adherence with clear ticket resolution trends — all in one place.
                        </p>
                    </section>

                    <section className="benefits-section">
                        <h2>Benefits of InspectXpert</h2>
                        <div className="benefits-grid">
                            <div className="benefit-item">
                                <span className="benefit-icon"><Users /></span>
                                <p>Improved team alignment & accountability</p>
                            </div>
                            <div className="benefit-item">
                                <span className="benefit-icon"><Target /></span>
                                <p>Centralized control for all departments and projects</p>
                            </div>
                            <div className="benefit-item">
                                <span className="benefit-icon"><Search /></span>
                                <p>Enhanced transparency and smooth execution</p>
                            </div>
                            <div className="benefit-item">
                                <span className="benefit-icon"><Zap />
                                </span>
                                <p>Reduced manual work through automated workflows</p>
                            </div>
                            <div className="benefit-item">
                                <span className="benefit-icon"><ChartColumn />
                                </span>
                                <p>Better decision-making with data-backed insights</p>
                            </div>
                            <div className="benefit-item">
                                <span className="benefit-icon"><Rocket /></span>
                                <p>Faster task completion with automated scheduling</p>
                            </div>
                        </div>
                    </section>

                    <section className="cta-section">
                        <h2 style={{ color: 'white' }}>Ready to Get Started?</h2>
                        <p style={{ color: 'white' }}>Join teams around the world who trust InspectXpert for their workflow management</p>
                        <button onClick={() => navigate('/login')} className="cta-button">
                            Get Started Today
                        </button>
                    </section>
                </div>
            </main>

            {/* Footer */}
            <footer className="home-footer">
                <div className="footer-content">
                    <div className="footer-left">
                        <h3>Contact Us</h3>
                        <div className="contact-info">
                            <div className="contact-item">
                                <span className="contact-icon"><MapPinHouse /></span>
                                <div>
                                    <strong>Address:</strong>
                                    <p>314, 3rd Floor, 99 Shopping Center, Sayan Rd, opp. Bhagunagar, Brahmani Nagar Society, Amroli<br />Surat, Gujarat 394107</p>
                                </div>
                            </div>
                            <div className="contact-item">
                                <span className="contact-icon"><Mail /></span>
                                <div>
                                    <strong>Email:</strong>
                                    <p>sales@synctel.in</p>
                                </div>
                            </div>
                            <div className="contact-item">
                                <span className="contact-icon"> <Phone /></span>
                                <div>
                                    <strong>Phone:</strong>
                                    <p>
                                        +91 6358280506
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="footer-right">
                        <h3>Legal</h3>
                        <div className="footer-links">
                            <a
                                href={`${process.env.REACT_APP_FRONTEND}/privacy-policy`}
                                className="footer-link"
                            >
                                Privacy Policy
                            </a>

                            <a
                                href={`${process.env.REACT_APP_FRONTEND}/terms-of-service`}
                                className="footer-link"
                            >
                                Terms & Conditions
                            </a>
                        </div>
                        <div className="footer-copyright">
                            <p>© 2026 InspectXpert. All rights reserved.</p>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Homepage;